import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { ConfirmedValidator } from '@nx-ahs/shared/validators';
import {
	ConfirmationModalService,
	ModalTemplateComponent,
	AppSpinnerComponent,
	UserProfilePicComponent,
	DbIconComponent
} from '@nx-ahs/shared/components';
import { MomentPipe } from '@nx-ahs/shared/pipes';
import { EntityNote, EntityNoteEntity, NoteService } from '@nx-ahs/notes';
import { Entity } from '@nx-ahs/entity/models';
import { AhsTranslationService } from '@nx-ahs/base-translate';
//import { MailService } from '../services/mail.service';

@Component({
	selector: 'app-view-batch-notes',
	templateUrl: './view-batch-notes.component.html',
	styleUrls: ['./view-batch-notes.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatCardModule,
		MatTooltipModule,
		FontAwesomeModule,
		ModalTemplateComponent,
		AppSpinnerComponent,
		UserProfilePicComponent,
		DbIconComponent,
		MomentPipe
	]
})
export class ViewBatchNotesComponent implements OnInit {
	public title: string;
	public loading: boolean = true;
	public config: any;
	public formGroup: FormGroup;
	public newNotesForm: FormGroup;
	public notePreviewLength: number = 100;
	public showSave: boolean = false;
	public disableSave: boolean = true;
	public showCancel: boolean = false;
	public showNewNote: boolean = false;
	public newNoteText: string;

	public mailBatchId: number;
	public entityNameId: number;
	public entityNotes: EntityNote[];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { notes: EntityNote[]; config: any },
		//private mailService: MailService,
		public matDialogRef: MatDialogRef<ViewBatchNotesComponent>,
		private noteService: NoteService,
		private formBuilder: FormBuilder,
		private confirmationModalService: ConfirmationModalService,
		private translateService: AhsTranslationService
	) {
		this.mailBatchId = data['mailBatchId'];
		this.entityNameId = 58;
	}

	ngOnInit(): void {
		// Set form title
		this.title = 'BatchId ' + this.mailBatchId.toString() + ' Notes';

		// Create form
		this.formGroup = this.formBuilder.group({
			note: [this.newNoteText, Validators.required]
		});

		this.getNotes(this.entityNameId, this.mailBatchId)
			.then((noteResults) => {
				if (noteResults.length > 0) {
					this.entityNotes = noteResults;

					this.entityNotes.forEach((n) => {
						//expand the notes that are too long initially
						n.componentExpanded = true;
					});
				}
			})
			.finally(() => {
				this.loading = false;
			});

		this.matDialogRef.keydownEvents().subscribe((event) => {
			if (event.key === 'Escape') {
				this.cancelClick();
			}
		});

		this.matDialogRef.backdropClick().subscribe(() => {
			this.cancelClick();
		});

		this.matDialogRef.disableClose = true;
	}

	public addNewNoteClick() {
		this.showNewNote = true;
		this.showSave = true;
		this.showCancel = true;
	}

	public notesChangedEvent(eventData: any) {
		const newNote = this.formGroup.get('note');
		if (newNote.value.length > 0) {
			this.disableSave = false;
		} else {
			this.disableSave = true;
		}
	}

	public cancelClick() {
		this.confirmationModalService
			.showConfirmationDialog(
				this.translateService.translate.instant('Confirm'),
				this.translateService.translate.instant('Click Ok to continue'),
				false,
				true
			)
			.beforeClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					this.showNewNote = false;
					this.showSave = false;
					this.showCancel = false;
					this.matDialogRef.close(false);
				}
			});
	}

	public saveClick() {
		if (!this.disableSave) {
			//Manage buttons
			this.showNewNote = false;
			this.showSave = false;
			this.showCancel = false;

			// Proceed if user typed something
			if (this.formGroup.valid) {
				const entityNoteEntity: any = [
					{
						linkedEntityNameId: this.entityNameId,
						linkedEntityId: this.mailBatchId
					}
				];
				const entityNote = new EntityNote(this.formGroup.get('note').value, entityNoteEntity);
				entityNote.isHighImportance = false;
				entityNote.requiresAcknowledgement = false;

				this.loading = true;
				this.noteService
					.saveEntityNote(entityNote)
					.toPromise()
					.then(
						(result) => {},
						(error) => {
							//this.snackBar.open('Adding note failed', 'DISMISS', { duration: 5000 });
						}
					)
					.then(() => {
						this.getNotes(this.entityNameId, this.mailBatchId).then((noteResults) => {
							if (noteResults.length > 0) {
								this.entityNotes = noteResults;

								this.entityNotes.forEach((n) => {
									//expand the notes that are too long initially
									n.componentExpanded = true;
								});
							}
						});
					})
					.finally(() => {
						this.loading = false;
					});
			}
		}
	}

	public toggleExpansion(note: any): void {
		note.componentExpanded = !note.componentExpanded;
	}

	private getNotes(entityNameId, entityId): Promise<EntityNote[]> {
		const entities = [new Entity(entityNameId, entityId)];
		return this.noteService
			.getNotes(entities, null, null) // Get all Notes
			.toPromise();
	}
}
